// src/pages/RegisterPage.js
// Public route: /register?phone={{phone}}

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsentCapture from '../components/ConsentCapture';
import Icon from '../components/Icon';
import previuLogo from '../assets/previu-logo.jpeg';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  bloodType: '', phone: '', email: '', country: 'IN',
  emergencyContactName: '', emergencyContactPhone: '', emergencyRelationship: '',
  medicalHistory: '', allergies: '', currentMedications: '',
  status: 'ACTIVE',
};

function Field({ label, required, htmlFor, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label htmlFor={htmlFor} style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        fontFamily: 'var(--font-body)',
      }}>
        {label} {required && <span style={{ color: 'var(--red)' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputBase = {
  padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)', fontSize: 14,
  color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  width: '100%', boxSizing: 'border-box',
};

function Input({ id, value, onChange, type = 'text', placeholder, required, readOnly, autoComplete, max, min }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      id={id} type={type} value={value} onChange={onChange}
      placeholder={placeholder} required={required} readOnly={readOnly}
      autoComplete={autoComplete} max={max} min={min}
      style={{
        ...inputBase,
        borderColor:  focused ? 'var(--brand-accent)' : 'var(--border)',
        boxShadow:    focused ? '0 0 0 3px rgba(184,132,58,0.12)' : 'none',
        background:   readOnly ? 'var(--bg-hover)' : 'var(--bg-subtle)',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function Select({ id, value, onChange, children, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      id={id} value={value} onChange={onChange} required={required}
      style={{
        ...inputBase,
        borderColor: focused ? 'var(--brand-accent)' : 'var(--border)',
        boxShadow:   focused ? '0 0 0 3px rgba(184,132,58,0.12)' : 'none',
        cursor: 'pointer', appearance: 'none',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
}

function StepIndicator({ step }) {
  const steps = ['Your consent', 'Your details'];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28,
    }}>
      {steps.map((label, i) => {
        const idx   = i + 1;
        const done  = step > idx;
        const active = step === idx;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-body)',
                background: done || active ? 'var(--brand-dark)' : 'var(--bg-hover)',
                color:      done || active ? 'var(--text-inverse)' : 'var(--text-muted)',
                flexShrink: 0,
              }}>
                {done ? '✓' : idx}
              </div>
              <span style={{
                fontSize: 12, fontWeight: active ? 600 : 400,
                color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
              }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 1, background: 'var(--border)',
                margin: '0 10px',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep]         = useState(1);
  const [consentData, setConsentData] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess]   = useState(false);
  const [phonePrefilled, setPhonePrefilled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phone  = params.get('phone');
    if (phone) {
      setForm(f => ({ ...f, phone }));
      setPhonePrefilled(true);
    }
  }, []);

  function handleConsentComplete(data) {
    setConsentData(data);
    setStep(2);
  }

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function validatePhone(raw, country) {
    const stripped = raw.replace(/[\s\-().]/g, '');

    if (!stripped) return { error: 'Phone number is required.' };
    if (!/^\d+$/.test(stripped.replace(/^\+/, '')))
      return { error: 'Phone number should contain digits only.' };

    if (country === 'AE') {
      let digits = stripped;
      if (digits.startsWith('+971')) digits = digits.slice(4);
      else if (digits.startsWith('971') && digits.length > 9) digits = digits.slice(3);

      if (digits.length !== 9)
        return { error: `Enter exactly 9 digits after the country code. You entered ${digits.length}.` };
      if (!/^5/.test(digits))
        return { error: `UAE mobile numbers must start with 5. "${digits[0]}" is not valid.` };

      return { phone: '971' + digits };
    }

    let digits = stripped;
    if (digits.startsWith('+91')) digits = digits.slice(3);
    else if (digits.startsWith('91') && digits.length > 10) digits = digits.slice(2);

    if (digits.length !== 10)
      return { error: `Enter exactly 10 digits after the country code. You entered ${digits.length}.` };
    if (!/^[6-9]/.test(digits))
      return { error: `Indian mobile numbers must start with 6, 7, 8, or 9. "${digits[0]}" is not valid.` };

    return { phone: '91' + digits };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!consentData?.consentGiven) {
      setError('Consent must be completed before registration.');
      return;
    }

    if (form.dateOfBirth && form.dateOfBirth > todayISO()) {
      setFieldErrors({ dateOfBirth: 'Date of birth cannot be in the future.' });
      return;
    }

    const phoneResult = validatePhone(form.phone, form.country);
    if (phoneResult.error) {
      setFieldErrors({ phone: phoneResult.error });
      return;
    }
    const normalisedPhone = phoneResult.phone;

    setFieldErrors({});
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/public/patients`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient: {
            ...form,
            phone: normalisedPhone,
            selfRegistered: true,
          },
          consentGiven: consentData.consentGiven,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 422 && body.error === 'CONSENT_REQUIRED') {
          setError('Consent is required. Please go back and accept the consent.');
        } else if (res.status === 409) {
          setError(body.message || 'A patient with this phone or email is already registered.');
        } else {
          setError(body.message || 'Registration failed. Please check your details and try again.');
        }
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch {
      setError('Could not connect to Previu. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="check" size={30} /></div>
            <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)', marginBottom: 8 }}>
              You're registered
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, fontFamily: 'var(--font-body)' }}>
              Your account has been created. Redirecting you to login…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={previuLogo} alt="Previu" style={{ height: 140, width: 'auto' }} />
          <div style={{
            fontSize: 11, color: 'var(--text-muted)', marginTop: -6,
            fontFamily: 'var(--font-body)',
          }}>
            Patient Registration
          </div>
        </div>

        <StepIndicator step={step} />

        {step === 1 && (
          <ConsentCapture onConsentComplete={handleConsentComplete} />
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--brand-accent)',
              fontFamily: 'var(--font-body)', marginBottom: 2,
            }}>
              Your details
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="First name" required htmlFor="reg-first-name">
                <Input id="reg-first-name" value={form.firstName} onChange={set('firstName')} placeholder="Rahul" required />
              </Field>
              <Field label="Last name" required htmlFor="reg-last-name">
                <Input id="reg-last-name" value={form.lastName}  onChange={set('lastName')}  placeholder="Sharma" required />
              </Field>
            </div>

            <Field label="Country" required htmlFor="reg-country">
              <Select
                id="reg-country"
                value={form.country}
                required
                onChange={e => {
                  setForm(f => ({ ...f, country: e.target.value, phone: phonePrefilled ? f.phone : '' }));
                  setFieldErrors(f => ({ ...f, phone: '' }));
                }}
              >
                <option value="IN">India</option>
                <option value="AE">United Arab Emirates</option>
              </Select>
            </Field>

            <Field label="Phone" required htmlFor="reg-phone">
              <Input id="reg-phone" value={form.phone}
                onChange={e => { set('phone')(e); setFieldErrors(f => ({ ...f, phone: '' })); }}
                type="tel"
                placeholder={form.country === 'AE' ? '501234567' : '9876543210'}
                autoComplete="off"
                required
                readOnly={phonePrefilled} />
              {fieldErrors.phone && (
                <span style={{ fontSize: 12, color: 'var(--red)', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                  {fieldErrors.phone}
                </span>
              )}
              {!fieldErrors.phone && !phonePrefilled && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                  {form.country === 'AE'
                    ? '9-digit number starting with 5. No need to add +971 — we handle that for you.'
                    : '10-digit number. No need to add +91 — we handle that for you.'}
                </span>
              )}
            </Field>

            <Field label="Email" required htmlFor="reg-email">
              <Input id="reg-email" value={form.email} onChange={set('email')}
                type="email" placeholder="rahul@example.com" required />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Date of birth" required htmlFor="reg-dob">
                <Input id="reg-dob" value={form.dateOfBirth}
                  onChange={e => { set('dateOfBirth')(e); setFieldErrors(f => ({ ...f, dateOfBirth: '' })); }}
                  type="date" required max={todayISO()} />
                {fieldErrors.dateOfBirth && (
                  <span style={{ fontSize: 12, color: 'var(--red)', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                    {fieldErrors.dateOfBirth}
                  </span>
                )}
              </Field>
              <Field label="Gender" required htmlFor="reg-gender">
                <Select id="reg-gender" value={form.gender} onChange={set('gender')} required>
                  <option value="">Select</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other / Prefer not to say</option>
                </Select>
              </Field>
            </div>

            <Field label="Blood type" htmlFor="reg-blood-type">
              <Select id="reg-blood-type" value={form.bloodType} onChange={set('bloodType')}>
                <option value="">Unknown</option>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </Select>
            </Field>

            {error && (
              <div role="alert" style={{
                fontSize: 13, color: 'var(--red)', padding: '10px 14px',
                borderRadius: 8, background: 'var(--red-bg)',
                fontFamily: 'var(--font-body)',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  flex: 1, border: '1.5px solid var(--border)',
                  borderRadius: 10, padding: '12px 18px', fontSize: 14,
                  fontWeight: 600, color: 'var(--text-primary)',
                  background: 'var(--bg-surface)', cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                }}
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 2, border: 'none', borderRadius: 10,
                  padding: '12px 18px', fontSize: 15, fontWeight: 600,
                  color: 'var(--text-inverse)', cursor: loading ? 'not-allowed' : 'pointer',
                  background: loading ? 'var(--text-muted)' : 'var(--brand-dark)',
                  fontFamily: 'var(--font-body)', transition: 'background 0.15s',
                }}
              >
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-navy) 100%)',
  display: 'flex', alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 16px 60px',
};

const cardStyle = {
  background: 'var(--bg-surface)',
  borderRadius: 16, padding: '32px 28px',
  width: '100%', maxWidth: 520,
  boxShadow: '0 4px 24px rgba(13,30,53,0.10)',
};
